import { businessWeekKeys, fillBar, sessionFill, shiftDateKey, timeOf, type AvailableSession, type SessionMutationResult, type TimetableEditor } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Callout,
  ConfirmDialog,
  ErrorState,
  FactRow,
  Field,
  InlineError,
  SkeletonList,
  StepArrows,
  showToast,
  useSessionEnded,
} from "../components/index.ts";
import { useSetSessionStaff } from "@teamna/web-client/hooks/use-groups.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOpenMemberThread } from "@teamna/web-client/hooks/use-messaging.ts";
import { useActiveBusiness, useCancelSession, useCreateSession, useMoveSession, useOwnerSessionAttendees, useOwnerSessions } from "@teamna/web-client/hooks/use-owner.ts";
import { useTimetableEditor } from "@teamna/web-client/hooks/use-timetable.ts";
import {
  dayKey,
  formatDateTime,
  formatDayLong,
  formatDayOfMonth,
  formatWeekRange,
  formatWeekdayShort,
  instantFromZonedTime,
} from "@teamna/web-client/lib/format.ts";
import { groupColor, sessionColor } from "../lib/group-color.ts";
import { AddLayer } from "./calendar/AddLayer.tsx";
import { CalendarBoard, type CalendarBlock, type CalendarColumn } from "./calendar/CalendarBoard.tsx";
import { shortTimeLabel, timeLabel } from "./calendar/layout.ts";
import { SessionCover } from "../covers/cover-requests.tsx";
import { SessionPeople, sessionFacts } from "./session-controls.tsx";
import { attendanceBadge } from "./today/progress.ts";
import { useSessionProgress } from "./today/use-today.ts";
import { CoachStrip, NO_ONE } from "./timetable/CoachStrip.tsx";
import { Back, CoachChoice, coachOptions, FieldBlock, teamOption } from "./timetable/Rail.tsx";

/**
 * The Sessions view of the Schedule page at 1024px and up: the sessions of one week, on the same
 * calendar as the Timetable view (`calendar/CalendarBoard.tsx`), with the same rail on the left and
 * the same coach row on top. A change here is for one session; the Timetable changes every week.
 *
 * - Seven columns in the business's week (`businessWeekKeys`, Saturday first), days off striped,
 *   one block per session in its level's colour. A cancelled session is struck through; a moved one
 *   is left out (the live one is in the week).
 * - A click opens the session in the rail: who is coming, add a person, the coach, move, cancel.
 * - A session that has not started follows the pointer to another day or time; the page then asks
 *   first, because a move tells everyone booked.
 * - Pick a level in the rail and click the week to add a one-off session there.
 *
 * Every time here is the business's own clock, never the laptop's.
 */

const DEFAULT_DAY = { start: 8 * 60, end: 20 * 60 };

function weekdayOfKey(key: string): number {
  return new Date(`${key}T00:00:00.000Z`).getUTCDay();
}

/** Minutes after local midnight in the business's timezone. */
export function localMinutes(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? 0);
  return (hour % 24) * 60 + minute;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function weekLabel(offset: number): string {
  if (offset === 0) return "This week";
  if (offset === 1) return "Next week";
  if (offset === -1) return "Last week";
  return offset > 0 ? `In ${offset} weeks` : `${Math.abs(offset)} weeks ago`;
}

export type Words = { group: string; groupPlural: string; staff: string; staffPlural: string; members: string; participant: string; participants: string };

/** The business's own words for the calendar rails (the Week view and the Day view). */
export function useScheduleWords(): Words {
  const { label } = useLabels();
  return {
    group: label("group"),
    groupPlural: label("group", { plural: true }),
    staff: label("staff"),
    staffPlural: label("staff", { plural: true }),
    members: label("member", { plural: true }),
    participant: label("participant"),
    participants: label("participant", { plural: true }),
  };
}
type Level = TimetableEditor["groups"][number];
type Team = TimetableEditor["team"];
/** A one-off session the owner is adding: the level, the date and the start in minutes. */
type Fresh = { groupId: string; day: string; start: number };
type Spot = { id: string; column: string; start: number; end: number };

/**
 * The week calendar of the Sessions page. With `staffUserId` (the page of one team member) it shows
 * only that person's sessions: no team row, and no level picker to add a session; a click still
 * opens the session.
 */
export function SessionsView({
  businessId,
  timezone,
  onTimetable,
  staffUserId,
}: {
  businessId: string;
  timezone: string;
  onTimetable?: () => void;
  staffUserId?: string;
}) {
  // The front desk (T1) does not read the timetable editor: its week has no group colours and no
  // team row, and it does not add or move a session.
  const { may } = useActiveBusiness();
  const readsEditor = may("readTimetableEditor");
  const editor = useTimetableEditor(readsEditor ? businessId : null);
  if (!readsEditor) return <SessionsWeek businessId={businessId} timezone={timezone} editor={null} onTimetable={onTimetable} staffUserId={staffUserId} />;
  if (editor.isLoading && !editor.data) return <SkeletonList rows={4} />;
  if (!editor.data) return <ErrorState error={editor.error} onRetry={() => void editor.refetch()} />;
  return <SessionsWeek businessId={businessId} timezone={timezone} editor={editor.data} onTimetable={onTimetable} staffUserId={staffUserId} />;
}

function SessionsWeek({
  businessId,
  timezone,
  editor,
  onTimetable,
  staffUserId,
}: {
  businessId: string;
  timezone: string;
  /** Null for a role that does not read the timetable editor (the front desk). */
  editor: TimetableEditor | null;
  onTimetable?: () => void;
  /** Only the sessions this person runs (the page of a team member). */
  staffUserId?: string;
}) {
  const { may } = useActiveBusiness();
  const movable = may("moveSession");
  const moveSession = useMoveSession(businessId);
  const [weekOffset, setWeekOffset] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  // The level picked to add a one-off session, and the one-off waiting for its details.
  const [brush, setBrush] = useState<string | null>(null);
  const [fresh, setFresh] = useState<Fresh | null>(null);
  // Where a dragged session is drawn, while the drag runs and while the move waits for a yes.
  const [preview, setPreview] = useState<Spot | null>(null);
  const [pendingMove, setPendingMove] = useState<{ session: AvailableSession; startsAt: string } | null>(null);
  const [hoverCoach, setHoverCoach] = useState<string | null>(null);
  const [pinnedCoach, setPinnedCoach] = useState<string | null>(null);
  // Read once per mount: a session that starts while the page is open can still be dragged, and
  // the API refuses the move with its own words.
  const [now] = useState(() => Date.now());
  const [nowMinute] = useState(() => localMinutes(new Date().toISOString(), timezone));

  const words = useScheduleWords();

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setBrush(null);
      setPinnedCoach(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const todayKey = dayKey(new Date().toISOString(), timezone);
  // The business week (Saturday first), shared with the phone list and the mobile app.
  const week = useMemo(() => {
    const keys = businessWeekKeys(todayKey, weekOffset);
    return {
      keys,
      from: instantFromZonedTime(keys[0]!, 0, 0, timezone),
      to: instantFromZonedTime(shiftDateKey(keys[6]!, 1), 0, 0, timezone),
    };
  }, [todayKey, weekOffset, timezone]);

  const sessions = useOwnerSessions({ businessId, from: week.from, to: week.to });
  // The registers of the same week: a block says where attendance is, as on the Day view.
  const progressOf = useSessionProgress(businessId, week.from, week.to);
  const levels = editor?.groups ?? [];
  const team = editor?.team ?? [];
  const levelOf = (groupId: string) => levels.find((level) => level.id === groupId);
  const colorOf = (groupId: string, fallback: string | null, index: number) => sessionColor(levels, groupId, fallback, index);

  // A moved row is a record of where the session was; the live one is in the same week.
  // On the page of a team member, only the sessions that person runs.
  const visible = (sessions.data ?? []).filter(
    (session) => session.status !== "moved" && (staffUserId === undefined || session.staffUserId === staffUserId),
  );
  const highlight = hoverCoach ?? pinnedCoach;
  const dayLabel = (key: string) => formatDayLong(instantFromZonedTime(key, 12, 0, timezone), timezone);

  const columns: CalendarColumn[] = week.keys.map((key) => {
    const noon = instantFromZonedTime(key, 12, 0, timezone);
    const count = visible.filter((session) => session.status === "scheduled" && dayKey(session.startsAt, timezone) === key).length;
    const off = editor?.daysOff.includes(weekdayOfKey(key)) ?? false;
    return {
      key,
      title: `${formatWeekdayShort(noon, timezone)} ${formatDayOfMonth(noon, timezone)}`,
      subtitle: off ? "Day off" : plural(count, "session", "sessions"),
      label: `${formatDayLong(noon, timezone)}, ${off ? "day off" : plural(count, "session", "sessions")}`,
      off,
      today: key === todayKey,
    };
  });

  const blocks: CalendarBlock[] = visible.map((session, index) => {
    const start = localMinutes(session.startsAt, timezone);
    const length = Math.max(15, Math.round((Date.parse(session.endsAt) - Date.parse(session.startsAt)) / 60_000));
    const day = dayKey(session.startsAt, timezone);
    const cancelled = session.status === "cancelled";
    // Booked of the places, as a bar along the lower edge (D38): one colour, gold when people wait.
    const seats = sessionFill({ kind: session.groupKind, capacity: session.capacity, bookedCount: session.bookedCount, waitlistCount: session.waitlistCount });
    const bar = cancelled ? null : fillBar(seats);
    // The places come first on the third line and are never cut (D38): "3/3 +2 waiting".
    const fill = `${seats.capacity === null ? `${seats.booked} booked` : seats.short}${seats.waiting > 0 ? ` +${seats.waiting} waiting` : ""}`;
    const register = progressOf.get(session.id);
    const attendance = register ? attendanceBadge(register.progress, register.session, timezone) : null;
    const spot = preview?.id === session.id ? preview : { column: day, start, end: Math.min(24 * 60, start + length) };
    return {
      key: session.id,
      id: session.id,
      column: spot.column,
      start: spot.start,
      end: spot.end,
      color: colorOf(session.groupId, session.groupColor, index),
      meter: bar,
      label: [
        `${session.groupName} at ${timeLabel(start)}`,
        dayLabel(day),
        session.staffName ?? `no ${words.staff}`,
        seats.capacity === null ? `${seats.booked} booked` : seats.text,
        cancelled ? "cancelled" : null,
        attendance?.label ?? null,
      ]
        .filter(Boolean)
        .join(", "),
      time: shortTimeLabel(spot.start),
      title: session.groupName,
      count: attendance?.label ?? fill,
      detail: session.staffName ?? "No one yet",
      struck: cancelled,
      selected: openId === session.id,
      faded: highlight !== null && (session.staffUserId ?? NO_ONE) !== highlight,
      movable: movable && !brush && session.status === "scheduled" && Date.parse(session.startsAt) > now,
      inert: brush !== null && !cancelled,
    };
  });
  const freshLevel = fresh ? levelOf(fresh.groupId) : undefined;
  if (fresh && freshLevel) {
    blocks.push({
      key: "new",
      id: "new",
      column: fresh.day,
      start: fresh.start,
      end: Math.min(24 * 60, fresh.start + freshLevel.durationMin),
      color: groupColor(freshLevel.color, freshLevel.colorIndex),
      label: `New ${freshLevel.name} at ${timeLabel(fresh.start)}, ${dayLabel(fresh.day)}`,
      time: shortTimeLabel(fresh.start),
      title: freshLevel.name,
      detail: "New, not added yet",
      selected: true,
    });
  }

  const range = useMemo(() => {
    let start = DEFAULT_DAY.start;
    let end = DEFAULT_DAY.end;
    for (const block of blocks) {
      start = Math.min(start, Math.floor(block.start / 60) * 60);
      end = Math.max(end, Math.ceil(block.end / 60) * 60);
    }
    return { start, end };
    // The hours only grow with the sessions of the week; a drag never changes them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions.data, fresh]);

  const open = openId ? (sessions.data ?? []).find((row) => row.id === openId) ?? null : null;
  const brushLevel = brush ? levelOf(brush) : undefined;

  async function confirmMove() {
    if (!pendingMove) return;
    try {
      const result = await moveSession.mutateAsync({ sessionId: pendingMove.session.id, startsAt: pendingMove.startsAt });
      showToast(moveSummary(result, timezone, words.members));
    } catch {
      showToast("We could not move the session. Nothing changed. Try again, or use Move in the session.", { tone: "info" });
    } finally {
      setPendingMove(null);
      setPreview(null);
    }
  }

  const rangeText = formatWeekRange(
    instantFromZonedTime(week.keys[0]!, 12, 0, timezone),
    instantFromZonedTime(week.keys[6]!, 12, 0, timezone),
    timezone,
  );

  let rail;
  if (open) {
    rail = (
      <SessionRail
        key={open.id}
        session={open}
        level={levelOf(open.groupId)}
        team={team}
        businessId={businessId}
        timezone={timezone}
        words={words}
        onClose={() => setOpenId(null)}
      />
    );
  } else if (fresh && freshLevel) {
    rail = (
      <NewSessionRail
        key={`${fresh.day}|${fresh.start}|${fresh.groupId}`}
        level={freshLevel}
        fresh={fresh}
        businessId={businessId}
        timezone={timezone}
        dayLabel={dayLabel(fresh.day)}
        onTime={(start) => setFresh({ ...fresh, start })}
        onCancel={() => setFresh(null)}
        onAdded={(id) => {
          setFresh(null);
          setOpenId(id);
        }}
      />
    );
  } else if (!may("addSession") || staffUserId !== undefined || !onTimetable) {
    rail = <p className="rounded-[10px] bg-sunken px-2.5 py-2 text-[12px] text-ink-2">Click a session to see who is coming, book a {words.participant} or check people in.</p>;
  } else {
    rail = (
      <LevelPicker
        levels={levels}
        sessions={visible}
        brush={brush}
        words={words}
        onBrush={(groupId) => {
          setBrush(groupId);
          setOpenId(null);
        }}
        onTimetable={onTimetable}
      />
    );
  }

  // One person's week has no tools to show until a session is open: the board takes the width.
  const railShown = staffUserId === undefined || open !== null;

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <StepArrows unit="week" onPrevious={() => setWeekOffset((value) => value - 1)} onNext={() => setWeekOffset((value) => value + 1)} />
        <h2 className="text-[17px] font-semibold">
          {weekLabel(weekOffset)} <span className="font-sans text-[14px] font-normal text-ink-2">{rangeText}</span>
        </h2>
        {weekOffset !== 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>
            Back to this week
          </Button>
        ) : null}
        <p className="ml-auto text-[12.5px] text-ink-3">
          {sessions.data
            ? [
                `${plural(visible.filter((row) => row.status === "scheduled").length, "session", "sessions")}.`,
                staffUserId !== undefined ? "Click a session to see who is coming." : null,
                movable ? "Drag a session to move it." : null,
              ]
                .filter(Boolean)
                .join(" ")
            : ""}
        </p>
      </div>

      <div
        className={`relative grid ${staffUserId === undefined ? "h-[calc(100vh-252px)] min-h-[560px]" : "h-[620px]"} ${
          railShown ? "grid-cols-[300px_minmax(0,1fr)]" : "grid-cols-[minmax(0,1fr)]"
        } grid-rows-[minmax(0,1fr)] overflow-clip rounded-card border border-line bg-paper shadow-card`}
      >
        {railShown ? (
          <aside aria-label="Session tools" className="grid content-start gap-3 overflow-y-auto border-r border-line p-3.5">
            {rail}
          </aside>
        ) : null}
        <div className="flex min-h-0 min-w-0 flex-col">
          {staffUserId === undefined ? (
            <CoachStrip
            items={visible.filter((session) => session.status === "scheduled").map((session) => ({ staffUserId: session.staffUserId, sessions: 1 }))}
            team={team}
            staffPlural={words.staffPlural}
            highlight={highlight}
            pinned={pinnedCoach}
            onHover={setHoverCoach}
            onPin={setPinnedCoach}
            />
          ) : null}
          <section aria-label="Sessions this week" className="relative min-h-0 flex-1 overflow-auto bg-surface">
            {sessions.isLoading && !sessions.data ? (
              <div className="p-4">
                <SkeletonList rows={4} />
              </div>
            ) : sessions.error && !sessions.data ? (
              <div className="p-4">
                <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
              </div>
            ) : (
              <CalendarBoard
                columns={columns}
                blocks={blocks}
                usualHours={range}
                nowMinute={nowMinute}
                // Not while a one-off is added or a move waits for a yes.
                hoverPreview={!brush && !fresh && !pendingMove}
                onSelect={(id) => {
                  if (id === "new") return;
                  setFresh(null);
                  setOpenId(id);
                }}
                onDragStart={() => setOpenId(null)}
                onDragMove={(block, change) => setPreview({ id: block.id, ...change })}
                onDragEnd={(block, change) => {
                  const session = visible.find((row) => row.id === block.id);
                  if (!session || (change.column === block.column && change.start === block.start)) {
                    setPreview(null);
                    return;
                  }
                  setPreview({ id: block.id, ...change });
                  setPendingMove({ session, startsAt: instantFromZonedTime(change.column, Math.floor(change.start / 60), change.start % 60, timezone) });
                }}
                renderColumn={(column, context) =>
                  brushLevel && !column.off ? (
                    <AddLayer
                      label={`Add ${brushLevel.name} on ${dayLabel(column.key)}`}
                      length={brushLevel.durationMin}
                      name={brushLevel.name}
                      context={context}
                      onAdd={(minute) => {
                        setFresh({ groupId: brushLevel.id, day: column.key, start: minute });
                        setBrush(null);
                      }}
                    />
                  ) : null
                }
              />
            )}
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={pendingMove !== null}
        title="Move this session?"
        confirmLabel="Move session"
        loading={moveSession.isPending}
        onCancel={() => {
          setPendingMove(null);
          setPreview(null);
        }}
        onConfirm={() => void confirmMove()}
      >
        {pendingMove ? (
          <>
            <p>
              {pendingMove.session.groupName}: {formatDateTime(pendingMove.session.startsAt, timezone)} to{" "}
              <strong className="text-ink">{formatDateTime(pendingMove.startsAt, timezone)}</strong>.
            </p>
            <p className="mt-2">{moveConsequence(pendingMove.session.bookedCount, words.members)}</p>
          </>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}

function moveConsequence(booked: number, members: string): string {
  if (booked === 0) return "Nobody is booked, so nobody gets a notification.";
  return `${plural(booked, "booking moves", "bookings move")} with it. The ${members} get a notification with the old time and the new time.`;
}

function moveSummary(result: SessionMutationResult, timezone: string, members: string): string {
  const when = formatDateTime(result.session.startsAt, timezone);
  if (result.affectedBookings === 0) return `Session moved to ${when}. Nobody was booked.`;
  return `Session moved to ${when}. ${plural(result.affectedBookings, "booking", "bookings")} moved with it, and ${result.affectedUsers} ${members} got a notification.`;
}

function cancelSummary(result: SessionMutationResult, members: string): string {
  if (result.affectedBookings === 0) return "Session cancelled. Nobody was booked.";
  return `Session cancelled. ${plural(result.affectedBookings, "booking", "bookings")} got the session back, and ${result.affectedUsers} ${members} got a notification.`;
}

// ---------------------------------------------------------------------------
// The rail: the levels, to add a one-off session
// ---------------------------------------------------------------------------

function LevelPicker({
  levels,
  sessions,
  brush,
  words,
  onBrush,
  onTimetable,
}: {
  levels: Level[];
  sessions: AvailableSession[];
  brush: string | null;
  words: Words;
  onBrush: (groupId: string | null) => void;
  onTimetable: () => void;
}) {
  const picked = brush ? levels.find((level) => level.id === brush) : undefined;
  return (
    <div className="grid gap-2.5">
      <div className="grid gap-0.5">
        <b className="text-[14px]">Add one session</b>
        <span className="text-[11.5px] text-ink-3">Pick a {words.group}, then click the week</span>
      </div>
      {levels.map((level) => {
        const count = sessions.filter((session) => session.groupId === level.id && session.status === "scheduled").length;
        const on = brush === level.id;
        return (
          <button
            key={level.id}
            type="button"
            aria-pressed={on}
            onClick={() => onBrush(on ? null : level.id)}
            className={[
              "grid grid-cols-[12px_minmax(0,1fr)_auto] items-center gap-x-2.5 rounded-xl border-[1.5px] px-2.5 py-2 text-left",
              on ? "border-accent bg-accent-surface" : "border-line bg-paper hover:border-line-strong",
            ].join(" ")}
          >
            <i className="row-span-2 h-8 w-3 rounded" style={{ background: groupColor(level.color, level.colorIndex) }} aria-hidden="true" />
            <b className="truncate text-[14px]">{level.name}</b>
            <span className="row-span-2 text-right text-[11.5px] text-ink-3 tabular-nums">{count} this week</span>
            <small className="col-start-2 text-[11.5px] text-ink-2">{level.durationMin} min</small>
          </button>
        );
      })}
      {picked ? (
        <>
          <p className="rounded-[10px] bg-accent-surface px-2.5 py-2 text-[12px] text-accent-strong">
            Click the week to add one {picked.name} session. It is for that date only.
          </p>
          <Button variant="ghost" size="sm" onClick={() => onBrush(null)} className="justify-self-start px-0">
            Stop adding
          </Button>
        </>
      ) : (
        <p className="rounded-[10px] bg-sunken px-2.5 py-2 text-[12px] text-ink-2">
          Click a session to see who is coming, change its {words.staff}, move it or cancel it. Drag it to move it.
        </p>
      )}
      <div className="mt-2 grid gap-1.5 rounded-[10px] border border-line px-2.5 py-2.5 text-[12px] text-ink-2">
        <b className="text-[13px] text-ink">Every week</b>
        <span>A change here is for one date. To change the weekly times from now on, use the Schedule view.</span>
        <Button variant="secondary" size="sm" onClick={onTimetable} className="justify-self-start">
          Open the schedule
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The rail: a one-off session to add
// ---------------------------------------------------------------------------

function NewSessionRail({
  level,
  fresh,
  businessId,
  timezone,
  dayLabel,
  onTime,
  onCancel,
  onAdded,
}: {
  level: Level;
  fresh: Fresh;
  businessId: string;
  timezone: string;
  dayLabel: string;
  onTime: (start: number) => void;
  onCancel: () => void;
  onAdded: (sessionId: string) => void;
}) {
  const createSession = useCreateSession(businessId);
  const [places, setPlaces] = useState("");
  const [error, setError] = useState<unknown>(null);
  // Blank takes the level's usual number, which can be null: no limit.
  const capacity = places.trim() === "" ? level.capacity : Number(places);
  const validPlaces = capacity === null || (Number.isInteger(capacity) && capacity > 0 && capacity <= 500);
  const startsAt = instantFromZonedTime(fresh.day, Math.floor(fresh.start / 60), fresh.start % 60, timezone);

  return (
    <div className="grid gap-3">
      <Back onClick={onCancel}>Cancel</Back>
      <div className="flex items-center gap-2">
        <i className="h-6 w-2.5 rounded" style={{ background: groupColor(level.color, level.colorIndex) }} aria-hidden="true" />
        <h2 className="text-[16px] font-bold">{level.name}</h2>
        <span className="rounded-full bg-accent-surface px-2 py-0.5 text-[10.5px] font-bold text-accent-strong">New</span>
      </div>
      <p className="text-[13px] text-ink-2">
        One session on {dayLabel}, {level.durationMin} min. The weekly times do not change.
      </p>
      {error ? <InlineError error={error} /> : null}
      <Field
        label="Starts"
        type="time"
        step={300}
        value={timeOf(fresh.start)}
        onChange={(event) => {
          const [hour, minute] = event.target.value.split(":").map(Number);
          if (hour !== undefined && minute !== undefined && !Number.isNaN(hour) && !Number.isNaN(minute)) onTime(hour * 60 + minute);
        }}
      />
      <Field
        label="Places"
        value={places}
        inputMode="numeric"
        maxLength={3}
        placeholder={level.capacity === null ? "No limit" : String(level.capacity)}
        onChange={(event) => setPlaces(event.target.value.replace(/[^0-9]/g, ""))}
        hint="Leave it blank to use the usual number."
      />
      <Button
        disabled={!validPlaces}
        loading={createSession.isPending}
        onClick={async () => {
          setError(null);
          try {
            const created = await createSession.mutateAsync({ groupId: level.id, startsAt, capacity });
            showToast(
              capacity === null
                ? `${level.name} added for ${formatDateTime(startsAt, timezone)}, with no limit on places.`
                : `${level.name} added for ${formatDateTime(startsAt, timezone)}, with ${capacity} places.`,
            );
            onAdded(created.id);
          } catch (caught) {
            setError(caught);
          }
        }}
      >
        Add the session
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The rail: one session
// ---------------------------------------------------------------------------

/**
 * One session: facts, the coach, who is coming, add a person, move, cancel. The move form is the
 * keyboard way to do what a drag does on the week: a date and a time in the business's clock. The
 * cancel view says the consequence before the owner commits: everyone gets the session back.
 */
/** The session in the rail: who is coming, the coach, move and cancel. The Week view and the Day view both open it. */
export function SessionRail({
  session,
  level,
  team,
  businessId,
  timezone,
  words,
  onClose,
  onTakeAttendance,
  stickyAttendance = false,
}: {
  session: AvailableSession;
  level: Level | undefined;
  team: Team;
  businessId: string;
  timezone: string;
  words: Words;
  onClose: () => void;
  /** Passed to the register: where Take attendance opens. Missing: the attendance route. */
  onTakeAttendance?: () => void;
  /** Keep Take attendance at the bottom of the panel (the Day view). */
  stickyAttendance?: boolean;
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"detail" | "move" | "cancel">("detail");
  // The facts change at the end (Came first): draw the panel again then (review F1.1).
  useSessionEnded(session.endsAt);
  // The front desk (T1) books and checks in here; the staff, a cover, a move or a cancel are the owner's.
  const { may } = useActiveBusiness();
  const [error, setError] = useState<unknown>(null);
  const [reason, setReason] = useState("");
  const [moveDate, setMoveDate] = useState(dayKey(session.startsAt, timezone));
  const [moveTime, setMoveTime] = useState(timeOf(localMinutes(session.startsAt, timezone)));

  const attendees = useOwnerSessionAttendees(businessId, session.id);
  const moveSession = useMoveSession(businessId);
  const cancelSession = useCancelSession(businessId);
  const setStaff = useSetSessionStaff(businessId);
  const openThread = useOpenMemberThread(businessId);

  const isLive = session.status === "scheduled";
  const Staff = `${words.staff.charAt(0).toUpperCase()}${words.staff.slice(1)}`;
  const [moveHour, moveMinute] = moveTime.split(":").map(Number);
  const validMove = /^\d{4}-\d{2}-\d{2}$/.test(moveDate) && /^\d{2}:\d{2}$/.test(moveTime);
  const movedStart = validMove ? instantFromZonedTime(moveDate, moveHour ?? 0, moveMinute ?? 0, timezone) : session.startsAt;
  const unchanged = movedStart === new Date(session.startsAt).toISOString();
  const color = level ? groupColor(level.color, level.colorIndex) : groupColor(session.groupColor, 0);

  function message(memberUserId: string) {
    setError(null);
    openThread.mutate(memberUserId, {
      onSuccess: (row) => void navigate({ to: "/owner/inbox/$conversationId", params: { conversationId: row.id } }),
      onError: setError,
    });
  }

  async function doMove() {
    setError(null);
    try {
      const result = await moveSession.mutateAsync({ sessionId: session.id, startsAt: movedStart });
      showToast(moveSummary(result, timezone, words.members));
      setMode("detail");
    } catch (caught) {
      setError(caught);
    }
  }

  async function doCancel() {
    setError(null);
    try {
      const result = await cancelSession.mutateAsync({ sessionId: session.id, reason: reason.trim() });
      showToast(cancelSummary(result, words.members));
      onClose();
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <div className="grid gap-3">
      <Back onClick={mode === "detail" ? onClose : () => setMode("detail")}>{mode === "detail" ? "Done" : "Back"}</Back>
      <div className="grid gap-0.5">
        <div className="flex items-center gap-2">
          <i className="h-6 w-2.5 rounded" style={{ background: color }} aria-hidden="true" />
          <h2 className="text-[16px] font-bold">{mode === "move" ? "Move this session" : mode === "cancel" ? "Cancel this session" : session.groupName}</h2>
        </div>
        <p className="text-[12.5px] text-ink-2">{formatDateTime(session.startsAt, timezone)}</p>
      </div>
      {error ? <InlineError error={error} /> : null}

      {mode === "detail" ? (
        <>
          {!isLive ? (
            <Callout title={session.status === "moved" ? "This session was moved" : "This session was cancelled"}>
              {session.status === "moved" ? "Everyone booked went with it to the new time." : (session.cancelReason ?? "Everyone booked got the session back.")}
            </Callout>
          ) : null}
          <div>
            {/* After the end: Came first, then Booked (`sessionFacts`, #ai-feedback 2026-10-26). The places left show on the calendar. */}
            {sessionFacts(session)
              .filter((fact) => fact.label !== "Spots left")
              .map((fact) => (
                <FactRow key={fact.label} label={fact.label} value={fact.value} />
              ))}
            <FactRow label="Runs for" value={`${session.durationMin} min`} />
            <FactRow label="Source" value={session.templateId ? "Weekly session" : "One off"} last />
          </div>
          {isLive && may("setSessionStaff") ? (
            <FieldBlock label={Staff}>
              <CoachChoice
                label={`${words.staff} for this session`}
                value={session.staffUserId ?? ""}
                options={[...(level ? coachOptions(team, level, words.staffPlural) : team.map(teamOption)), { value: "", label: "No one" }]}
                onChange={(userId) => {
                  setError(null);
                  setStaff.mutate(
                    { sessionId: session.id, staffUserId: userId || null },
                    {
                      onSuccess: (row) => showToast(row.staffName ? `${row.staffName} runs this session now.` : `This session has no ${words.staff} now.`),
                      onError: setError,
                    },
                  );
                }}
              />
            </FieldBlock>
          ) : null}
          {isLive && may("askCover") ? <SessionCover businessId={businessId} sessionId={session.id} timezone={timezone} /> : null}

          <SessionPeople
            businessId={businessId}
            session={session}
            attendees={attendees}
            timezone={timezone}
            onMessage={message}
            onTakeAttendance={onTakeAttendance}
            sticky={stickyAttendance}
            onError={setError}
            headClassName="pt-2"
          />

          {isLive && (may("moveSession") || may("cancelSession")) ? (
            <div className="flex flex-wrap gap-2 pt-2">
              {may("moveSession") ? (
                <Button size="sm" onClick={() => setMode("move")}>
                  Move session
                </Button>
              ) : null}
              {may("cancelSession") ? (
                <Button size="sm" variant="danger" onClick={() => setMode("cancel")}>
                  Cancel session
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {mode === "move" ? (
        <>
          <p className="text-[13px] text-ink-2">Choose the new date and start time, or drag the session on the week. Times are the business's own clock.</p>
          <Field label="Date" type="date" value={moveDate} onChange={(event) => setMoveDate(event.target.value)} />
          <Field label="Start time" type="time" step={300} value={moveTime} onChange={(event) => setMoveTime(event.target.value)} />
          <div className="rounded-card bg-accent-surface px-3 py-2.5">
            <p className="eyebrow">New time</p>
            <p className="text-[16px] font-semibold text-accent-strong">{formatDateTime(movedStart, timezone)}</p>
            <p className="text-[12.5px] text-ink-2">was {formatDateTime(session.startsAt, timezone)}</p>
          </div>
          <p className="text-[12.5px] text-ink-2">{moveConsequence(session.bookedCount, words.members)}</p>
          <Button disabled={!validMove || unchanged} loading={moveSession.isPending} onClick={() => void doMove()}>
            {session.bookedCount === 0 ? "Move the session" : "Move and notify everyone"}
          </Button>
        </>
      ) : null}

      {mode === "cancel" ? (
        <>
          <Callout tone="late" title="Everyone gets the session back">
            {session.bookedCount === 0
              ? "Nobody is booked, so nobody gets a notification."
              : `${plural(session.bookedCount, `${words.participant} is`, `${words.participants} are`)} booked. Everyone gets the session back, and the ${words.members} get a notification.`}
          </Callout>
          <Field
            label={`Reason, shown to ${words.members} (optional)`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Venue maintenance"
            maxLength={200}
          />
          <Button variant="danger" loading={cancelSession.isPending} onClick={() => void doCancel()}>
            {session.bookedCount === 0 ? "Cancel the session" : "Cancel and notify everyone"}
          </Button>
        </>
      ) : null}
    </div>
  );
}
